import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { succesToast, errorToast } from 'basics/utils/toast';
import { EventStatus } from 'generated/event.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const unpublishEventMutationCall = gql`
mutation UnpublishEvent($eventId: String!) {
  unpublishEvent(eventId: $eventId)
}`;

type ResultType = {
  unpublishEvent: EventStatus;
};

type UseUnpublishEventMutation = [
  (eventId: string) => Promise<EventStatus | undefined | null>,
  MutationResult<ResultType>,
];

export const useUnpublishEventMutation = (): UseUnpublishEventMutation => {
  const { t } = useTranslation();
  const [unpublishEvent, state] = useMutation<ResultType>(unpublishEventMutationCall);
  const executeMutation = useCallback(async (eventId: string) => {
    try {
      const { data } = await unpublishEvent({
        variables: { eventId },
      });
      succesToast(t('toast_unpublish_event_success'));

      return data?.unpublishEvent;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, { eventId });
        Logger.logError({
          error,
          customContext,
          transactionName: 'unpublishEvent.ts - useUnpublishEventMutation',
        });
      }
      errorToast(t('toast_unpublish_event_error'));
      return null;
    }
  }, [unpublishEvent, t]);

  return [executeMutation, state];
};
